/**
 * ComplianceCoverageRing — SVG donut chart showing compliance status
 * distribution. Renders as a compact ring with the compliance score
 * centered.
 *
 * Segments: emerald (addressed), amber (partial), rose (not addressed),
 * slate (not started / N/A).
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function ComplianceCoverageRing({ addressed, partial, notAddressed, total, size = 96 }) {
    if (total === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center rounded-full bg-slate-100 text-xs text-slate-400",
            style: {
                width: size,
                height: size
            },
            children: "No data"
        });
    }
    const score = (addressed / total * 100).toFixed(2);
    const radius = 15.9;
    const _circumference = 2 * Math.PI * radius;
    // Calculate segment percentages.
    const pctAddressed = addressed / total * 100;
    const pctPartial = partial / total * 100;
    const pctNotAddressed = notAddressed / total * 100;
    const pctRemaining = 100 - pctAddressed - pctPartial - pctNotAddressed;
    // Each segment starts where the previous one ended.
    // strokeDashoffset shifts the start point (25 = top of circle).
    const segments = [
        {
            pct: pctAddressed,
            color: '#10b981',
            label: 'Addressed'
        },
        {
            pct: pctPartial,
            color: '#f59e0b',
            label: 'Partial'
        },
        {
            pct: pctNotAddressed,
            color: '#f43f5e',
            label: 'Not addressed'
        },
        {
            pct: pctRemaining,
            color: '#e2e8f0',
            label: 'Remaining'
        }
    ];
    let offset = 25; // Start from top
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative inline-flex items-center justify-center",
        style: {
            width: size,
            height: size
        },
        children: [
            /*#__PURE__*/ _jsxs("svg", {
                viewBox: "0 0 36 36",
                className: "h-full w-full -rotate-90",
                "aria-hidden": "true",
                role: "img",
                children: [
                    /*#__PURE__*/ _jsx("title", {
                        children: "Compliance coverage"
                    }),
                    segments.map((seg, i)=>{
                        if (seg.pct <= 0) return null;
                        const dashArray = `${seg.pct} ${100 - seg.pct}`;
                        const el = /*#__PURE__*/ _jsx("circle", {
                            cx: "18",
                            cy: "18",
                            r: radius,
                            fill: "none",
                            strokeWidth: "3",
                            stroke: seg.color,
                            strokeDasharray: dashArray,
                            strokeDashoffset: offset,
                            strokeLinecap: "round",
                            className: "transition-all duration-500"
                        }, i);
                        offset -= seg.pct;
                        return el;
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "absolute inset-0 flex flex-col items-center justify-center",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-xl font-bold tabular-nums text-slate-900",
                        children: [
                            score,
                            "%"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-[9px] font-medium uppercase tracking-wide text-slate-400",
                        children: "compliant"
                    })
                ]
            })
        ]
    });
}
/**
 * Ring legend — small inline dots with counts.
 */ export function RingLegend({ items }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "flex flex-wrap gap-x-4 gap-y-1",
        children: items.map((item)=>/*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-1.5 text-[11px]",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "h-2 w-2 rounded-full",
                        style: {
                            backgroundColor: item.color
                        }
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-slate-600",
                        children: item.label
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-semibold tabular-nums text-slate-800",
                        children: item.count
                    })
                ]
            }, item.label))
    });
}
